import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { AppErrorBoundary } from './app/AppErrorBoundary';
import { appVersion } from './app/appVersion';
import { queryClient } from './app/queryClient';
import { router } from './app/router';
import { RecordTitleProvider } from './lib/recordTitle';
import { SessionProvider } from './lib/session/SessionProvider';
import { Toaster } from './ui/shadcn/sonner';
// The token layer, and the order is load-bearing. The ui barrel brings the contract (names
// plus neutral fallbacks) and the base layer; the Empire theme then overrides those properties
// with the brand values. Both land at :root with equal specificity, so swapping these two lines
// leaves the app rendering in neutral greys with nothing failing. Swapping theme-empire.css for
// another theme file — and changing nothing else — is FS-3 FR-T5's reuse proof.
//
// THIS IS THE LINE FS-3 AC-3 SWAPS. ./ui/styles/theme-neutral.css is a committed second theme kept for
// exactly that: point the import at it, screenshot, point it back. Nothing else in the app or in
// src/ui moves, which is the whole claim. Note what the swap costs and does not cost — the
// neutral theme names no brand face, so the app then downloads no woff2 at all (measured: two font
// requests under Empire, zero under neutral), while space/*, layout/* and z/* do not shift,
// because those are the contract's structural scales and a theme does not own them.
import './ui';
import './ui/styles/theme-empire.css';
// Revamp (AD #39): the revamped colour layer. It declares new token names only, and re-points the existing
// names only under <html data-et-theme="revamp">, which RouteTheme sets on routes whose handle carries
// `theme: 'revamp'`; every other page keeps the Empire theme above.
import './ui/styles/theme-revamp.css';
// shadcn/ui (AD #39): Tailwind's theme and utility layers for the shadcn components the revamped
// routes are built from, mapped onto the tokens above. No preflight, so no other page changes.
import './ui/styles/shadcn.css';

const container = document.getElementById('root');
if (!container) {
    // Not a non-null assertion: noNonNullAssertion is an error here, and a thrown message that
    // names the missing element is more useful at 2am than a TypeError on null.
    throw new Error('Root element #root is missing from index.html');
}

// Stamp the running bundle's version onto the document root so a deployed bundle stays identifiable
// from the browser (git-conventions § Release Process) without a visible UI element — the sidebar no
// longer shows a version label (F5-b finding F-001). Read it via
// `document.documentElement.dataset.appVersion` or the Elements panel.
document.documentElement.dataset.appVersion = appVersion();

createRoot(container).render(
    <StrictMode>
        {/* Outside RouterProvider on purpose: this has to catch a failure in the router itself,
            which an error element inside it cannot. */}
        <AppErrorBoundary>
            {/* The query client sits above the session and the router so every route reads one
                cache, and inside the error boundary so a failure in either is still caught. The
                session itself is not read through it: SessionProvider owns GET /api/v1/me and its
                re-reads, and clears this cache when the session ends or the user changes, so it
                must stay inside this provider (BUG-083). */}
            <QueryClientProvider client={queryClient}>
                {/* SessionProvider wraps the router so GET /api/v1/me runs once at the root
                    regardless of entry route; both the (F5-a WI-1.2) /login route and the
                    authenticated tree read the one session. */}
                <SessionProvider>
                    {/* Around the router: a record page publishes its record's name, and the
                        route's own page title (`RouteTitle`) reads it. */}
                    <RecordTitleProvider>
                        <RouterProvider router={router} />
                    </RecordTitleProvider>
                    {/* The one toast region (shadcn's Sonner, design-system §5): outside the router, so a
                        toast raised just before a navigation stays on screen after it. */}
                    <Toaster />
                </SessionProvider>
            </QueryClientProvider>
        </AppErrorBoundary>
    </StrictMode>,
);
